import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from "react-dom";
import { PlayerRef, PlayerRefWrapper, PlayerWrapper } from './Player.styled';
import { IPlayerConfig } from "models/player-config.model";
import videojsOptions from "config/videojs-options";
import { nuevoOptions } from "models/nuevo";
import nuevoOptionsConfig from 'config/nuevo-options';
import videojs, { VideoJsPlayer } from "video.js";
import { useLocation } from "react-router-dom";
import { IMedia } from "models/media.model";

// Importations nécessaires pour le player
import 'assets/nuevo/nuevo';
import 'assets/nuevo/thumbnails';
import 'assets/nuevo/visualizer';
import 'assets/nuevo/hlsjs';
import { PlayerReady } from "functions/PlayerReady";

import ChaptersContainer from "components/Chapters/Chapters";
import useFetchSourceHeaders from "hooks/fetchSourceHeaders";
import { vastVpaidPlugin } from "functions/PlayerPlugins";
import { checkImageURL } from "helpers/check-image.helper";
import { FullScreenCustomize } from "functions/FullScreenCustomize";
import { FormattedMediaForPlayer } from "functions/FormattedMediaForPlayer";
import { Chapters } from "functions/Chapters";
import { useDispatch } from "react-redux";
import { initChapters } from "store/features/chapters/chaptersSlice";
import useDisposePlayer from "hooks/disposePlayer";
import { configureAds } from "functions/ConfigurationAds";
import { hidePanelWhenleavePlayerEvent } from "events/global.events";
import PlayerSeo from "SEO/Helmet";
import { cleanUrlParams } from "helpers/mime-type.helper";
import { RegisterHlsXHRBeforeRequestEventHandler } from "functions/RegisterHlsXHRBeforeRequestEventHandler";

import '@videojs/http-streaming';
import DOMPurify from "dompurify";

interface PlayerProps {
  playerConfig: IPlayerConfig,
  onPlayerReady: (player: VideoJsPlayer) => void,
  page: 'share' | 'embed'
}

function Player({ playerConfig, onPlayerReady, page }: PlayerProps) {

  const dispatch = useDispatch();
  const videoRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const chapterPanelRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();

  const [showChaptersPanel, setShowChapters] = useState(false);
  const [videoSource, setVideoSource] = useState('');
  const currentMediaBaseOnSourceRef = React.useRef<IMedia | undefined>(undefined);

  const id = location.pathname.substring(
    window.location.pathname.lastIndexOf("/") + 1
  );

  const urlParams = new URLSearchParams(window.location.search);

  // @ts-ignore
  const params: Record<string, any> = Object.fromEntries([...urlParams.entries()]);
  let videojsOption = videojsOptions(playerConfig?.player, playerConfig?.playlist);
  const medias = FormattedMediaForPlayer(playerConfig.media as IMedia[], params);

  if (playerConfig?.media.length === 1 && medias !== undefined) {
    videojsOption.sources = medias[0]?.sources;
  }

  useEffect(() => {
    if (!playerRef.current) return;

    const isPlaylist = !!(playerRef.current as VideoJsPlayer).playlist()
    const playlistButton = document.querySelector('.vjs-playlist-button') as HTMLButtonElement;
    const playlistPanel = document.querySelector('.vjs-vplaylist') as HTMLElement;

    if (playlistButton) {
      if (isPlaylist && showChaptersPanel) {
        playlistButton.style.right = '402px';
        playlistPanel.style.right = '394px';
      } else {
        playlistButton.style.right = '2px';
        playlistPanel.style.right = '0';
      }
    }

  }, [showChaptersPanel])

  useEffect(() => {
    if (!playerRef.current) {
      const videoElement = document.createElement("video-js");
      (videoRef.current as unknown as HTMLElement)?.appendChild(videoElement);

      const playerVideo = (playerRef.current as unknown) = videojs(videoElement, videojsOption, () => {
        onPlayerReady(playerVideo);


        // Ajout du conteneur de chapitres dans la balise video-js
        if (videoElement && !chapterPanelRef.current) {
          const chapterContainer = document.createElement("div");
          chapterContainer.className = "vjs-chapter-panel";
          chapterPanelRef.current = chapterContainer;
          videoElement.appendChild(chapterContainer);
        }

        if (playerConfig.player.ads?.length) {
          vastVpaidPlugin().onload = () => {
            playerVideo?.vastAds(configureAds(playerConfig.player.ads));
          }
        }

        playerVideo.on('ready', () => {

          videoElement.setAttribute("playsinline", "true");
          videoElement.setAttribute("webkit-playsinline", "true");

          // Configuration de beforeRequest
          if (videojs.Vhs) {
            videojs.Vhs.xhr.beforeRequest = (opt) => {
              return RegisterHlsXHRBeforeRequestEventHandler(opt);
            };
          }
        });

        const handleTouchStart = () => {
          if (videoElement) {
            videoElement.classList.remove('vjs-touch-inactive');
          }
        };

        playerVideo.on('touchstart', handleTouchStart);
        const currentSource = playerVideo.currentSource().src;
        setVideoSource(currentSource);

        const mediaBasedOnSource = playerConfig.media.find((media) => {
          return media.source === cleanUrlParams(currentSource);
        });

        currentMediaBaseOnSourceRef.current = mediaBasedOnSource;

        const checkPoster = async () => {
          if (mediaBasedOnSource?.thumbnails.poster) {
            const isPosterValid = await checkImageURL(mediaBasedOnSource.thumbnails.poster);
            return !!isPosterValid;
          }
        };

        checkPoster().then((res) => {
          if (res) {

            if (playerConfig?.player.preload_image !== undefined) {
              playerVideo.poster(DOMPurify.sanitize(playerConfig?.player.preload_image));
            }

            if (mediaBasedOnSource?.thumbnails.poster as string !== undefined) {
              playerVideo.poster(DOMPurify.sanitize(mediaBasedOnSource?.thumbnails.poster as string));
            }
          }
        });

        if (playerConfig.media.length > 1) {
          playerVideo.playlist(medias);
        }

        PlayerReady({
          player: playerVideo,
          config: playerConfig,
          openChapterPanel: () => displayPanelChapter(),
        });

        window.player = playerVideo;
        window.videojs = videojs as any;

      }) as unknown as VideoJsPlayer;

      hidePanelWhenleavePlayerEvent(playerVideo);

      playerVideo.on('fullscreenchange', () => FullScreenCustomize(
        {
        player: playerVideo,
        config: playerConfig,
        currentMedia: currentMediaBaseOnSourceRef.current as IMedia,
        shareId: id, page
      }));

      const nuevoConfig: Partial<nuevoOptions> = nuevoOptionsConfig(
        playerVideo,
        id,
        playerConfig?.player,
        playerConfig?.media,
        playerConfig?.playlist
      );

      playerVideo.nuevo(nuevoConfig);

      playerVideo.one('play', async () => {
        if (playerConfig.media[0].chapters !== void 0) {
          Chapters({ url: playerConfig.media[0].chapters }).then((res) => {
            dispatch(initChapters(res));
          });
        }
      });

    } else {
      const player = playerRef.current as VideoJsPlayer;
      player.src(videojsOption.sources);
    }
  }, []);

  useFetchSourceHeaders(videoSource);
  useDisposePlayer(playerRef.current as unknown as VideoJsPlayer);

  const closePanelChapter = () => {
    setShowChapters(false);
  }

  const displayPanelChapter = () => {
    setShowChapters(true);
  }

  return (
    <>
      {currentMediaBaseOnSourceRef.current && (
        <PlayerSeo
          source={currentMediaBaseOnSourceRef.current.source}
          title={currentMediaBaseOnSourceRef.current.title}
          description={currentMediaBaseOnSourceRef.current.description}
          thumbnail={currentMediaBaseOnSourceRef.current.thumbnails?.poster || ''}
        />
      )}
      <PlayerWrapper>
        <PlayerRefWrapper data-vjs-player data-testid="Player">
          <PlayerRef ref={videoRef} id={`item-${id}`} className='ik-player-video-js'/>

          {chapterPanelRef.current && showChaptersPanel &&
            createPortal(<ChaptersContainer page={page} closePanel={() => closePanelChapter()} player={playerRef.current} />, chapterPanelRef.current)}
        </PlayerRefWrapper>
      </PlayerWrapper>
    </>
  );
}

export default Player;
